import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import type { AxiosResponse } from 'axios';
import type ApiPreferenceOption from 'src/core/models/api/ApiPreferenceOption';
import type ApiPreferencesResponse from 'src/core/models/api/responses/ApiPreferencesResponse';
import ThermenRepository from 'src/data/repositories/ThermenRepository';
import toErrorMessage from 'src/core/utils/ErrorMessages';
import { reportToCallerOrCrashlytics } from 'src/domain/stores/StoreErrorChannel';
import type ApiRequestError from 'src/core/models/api/ApiRequestError';

const withSelection = (options: ApiPreferenceOption[] | null, id: string, selected: boolean) =>
	options?.map((option) => (option.id === id ? { ...option, selected } : option)) ?? null;

const selectionOf = (options: ApiPreferenceOption[] | null, id: string): boolean | undefined =>
	options?.find((option) => option.id === id)?.selected;

const initialState = {
	emailNotifications: null as ApiPreferenceOption[] | null,
	appNotifications: null as ApiPreferenceOption[] | null,
	preferredLanguages: null as ApiPreferenceOption[] | null,
	isLoading: false,
	errorMessage: null as string | null,
};

export const usePreferencesStore = create(
	combine({ ...initialState }, (set, get) => {
		const getApiPreferences = async ({
			onSuccess,
			onError,
			onFinally,
		}: {
			onSuccess?: (response: AxiosResponse<ApiPreferencesResponse>) => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		} = {}) => {
			const state = get();
			if (state.emailNotifications === null && state.appNotifications === null && state.preferredLanguages === null) {
				set({ isLoading: true });
			}
			set({ errorMessage: null });

			await ThermenRepository.getPreferences()
				.then(async (response) => {
					const data: ApiPreferencesResponse | null = response.data;
					const state = get();
					set({
						emailNotifications: data?.email_notifications ?? state.emailNotifications,
						appNotifications: data?.app_notifications ?? state.appNotifications,
						preferredLanguages: data?.preferred_language ?? state.preferredLanguages,
						isLoading: false,
					});
					await onSuccess?.(response);
				})
				.catch((error: ApiRequestError) => {
					set({ errorMessage: toErrorMessage(error), isLoading: false });
					reportToCallerOrCrashlytics({ message: 'Fetching the preferences failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const putApiPreferredLanguage = async ({
			id,
			onSuccess,
			onError,
			onFinally,
		}: {
			id: string;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			set({ isLoading: true, errorMessage: null });

			await ThermenRepository.setPreferredLanguage(id)
				.then(async () => {
					set({
						preferredLanguages:
							get().preferredLanguages?.map((language) => ({ ...language, selected: language.id === id })) ?? null,
						isLoading: false,
					});
					await onSuccess?.();
				})
				.catch((error: ApiRequestError) => {
					set({ errorMessage: toErrorMessage(error), isLoading: false });
					reportToCallerOrCrashlytics({ message: 'Saving the preferred language failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const putApiEmailNotificationPreference = async ({
			id,
			selected,
			onSuccess,
			onError,
			onFinally,
		}: {
			id: string;
			selected: boolean;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			const rollbackTo = selectionOf(get().emailNotifications, id);
			set({ emailNotifications: withSelection(get().emailNotifications, id, selected), errorMessage: null });

			await ThermenRepository.setEmailNotificationPreference(id, selected)
				.then(async () => await onSuccess?.())
				.catch((error: ApiRequestError) => {
					set({
						emailNotifications:
							rollbackTo === undefined ? get().emailNotifications : withSelection(get().emailNotifications, id, rollbackTo),
						errorMessage: toErrorMessage(error),
					});
					reportToCallerOrCrashlytics({ message: 'Saving an email notification preference failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const putApiAppNotificationPreference = async ({
			id,
			selected,
			onSuccess,
			onError,
			onFinally,
		}: {
			id: string;
			selected: boolean;
			onSuccess?: () => void | Promise<void>;
			onError?: (error: ApiRequestError) => void;
			onFinally?: () => void;
		}) => {
			const rollbackTo = selectionOf(get().appNotifications, id);
			set({ appNotifications: withSelection(get().appNotifications, id, selected), errorMessage: null });

			await ThermenRepository.setAppNotificationPreference(id, selected)
				.then(async () => await onSuccess?.())
				.catch((error: ApiRequestError) => {
					set({
						appNotifications:
							rollbackTo === undefined ? get().appNotifications : withSelection(get().appNotifications, id, rollbackTo),
						errorMessage: toErrorMessage(error),
					});
					reportToCallerOrCrashlytics({ message: 'Saving an app notification preference failed.', error, onError });
				})
				.finally(() => onFinally?.());
		};

		const reset = () => set({ ...initialState });

		return {
			getApiPreferences,
			putApiPreferredLanguage,
			putApiEmailNotificationPreference,
			putApiAppNotificationPreference,
			reset,
		};
	}),
);
